import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
 import { 
   type OrgStatus,
   type MemberStatus,
   type DealerRole,
   type AccessErrorCode,
   ACCESS_ERROR_CODES,
   DEALER_ROLES,
   ORG_STATUS,
   MEMBER_STATUS,
 } from '@/lib/constants';

 /**
  * Complete dealer scope information
  * Single source of truth for dealer access control on the client
  */
 export interface DealershipScope {
   // Core dealer state
   isDealer: boolean;
   isDealerAdmin: boolean;
   isSales: boolean;
  dealershipId: string | null;
  dealershipName: string | null;
  salespersonId: string | null;
   
   // Role (spec-compliant naming)
   role: DealerRole | null;
   
   // Status checks
   orgStatus: OrgStatus | null;
   memberStatus: MemberStatus | null;
   
   // Auth state (from AuthContext)
   isEmailVerified: boolean;
   accountStatus: 'active' | 'suspended' | null;
   
   // Loading state
   isLoading: boolean;
   
   // Computed access check (all requirements met)
   hasFullAccess: boolean;
   
   // Error code for debugging/display (null if has full access)
   errorCode: AccessErrorCode | null;
}

/**
 * Hook to determine the current user's dealership scope
 * Returns the dealership they belong to and their role within it
 * 
 * This is the authoritative source for dealership-scoped access control
  * 
  * Access Requirements (all must be true for hasFullAccess):
  * - isEmailVerified = true
  * - accountStatus = 'active'
  * - orgStatus = 'approved'
  * - memberStatus = 'active'
 */
export function useDealershipScope(): DealershipScope {
   const { user, isLoading: authLoading, isEmailVerified: authEmailVerified } = useAuth();
  const [scope, setScope] = useState<DealershipScope>({
     isDealer: false,
     isDealerAdmin: false,
     isSales: false,
    dealershipId: null,
    dealershipName: null,
     salespersonId: null,
    role: null,
     orgStatus: null,
     memberStatus: null,
     isEmailVerified: false,
     accountStatus: null,
    isLoading: true,
     hasFullAccess: false,
     errorCode: null,
  });

  useEffect(() => {
    if (authLoading) return;
    
    if (!user) {
      setScope({
         isDealer: false,
         isDealerAdmin: false,
         isSales: false,
        dealershipId: null,
        dealershipName: null,
         salespersonId: null,
        role: null,
         orgStatus: null,
         memberStatus: null,
         isEmailVerified: false,
         accountStatus: null,
        isLoading: false,
         hasFullAccess: false,
         errorCode: null,
      });
      return;
    }

    const fetchScope = async () => {
      try {
        // Check if user owns a dealer account (dealer admin)
        const { data: dealerAccount } = await supabase
          .from('dealer_accounts')
           .select('id, dealership_name, status')
          .eq('user_id', user.id)
          .maybeSingle();

        if (dealerAccount) {
           const orgStatus = dealerAccount.status as OrgStatus;
           const memberStatus = orgStatus === ORG_STATUS.APPROVED ? MEMBER_STATUS.ACTIVE : MEMBER_STATUS.INACTIVE;
           const hasFullAccess = 
             authEmailVerified && 
             orgStatus === ORG_STATUS.APPROVED;
           
           // Compute error code
           let errorCode: AccessErrorCode | null = null;
           if (!authEmailVerified) {
             errorCode = ACCESS_ERROR_CODES.EMAIL_NOT_VERIFIED;
           } else if (orgStatus === ORG_STATUS.SUSPENDED) {
             errorCode = ACCESS_ERROR_CODES.ORG_SUSPENDED;
           } else if (orgStatus === ORG_STATUS.PENDING) {
             errorCode = ACCESS_ERROR_CODES.ORG_NOT_APPROVED;
           }
           
          setScope({
             isDealer: true,
             isDealerAdmin: true,
             isSales: false,
            dealershipId: dealerAccount.id,
            dealershipName: dealerAccount.dealership_name,
            salespersonId: null,
             role: DEALER_ROLES.ADMIN,
             orgStatus,
             memberStatus,
             isEmailVerified: authEmailVerified,
             accountStatus: orgStatus === ORG_STATUS.SUSPENDED ? 'suspended' : 'active',
             isLoading: false,
             hasFullAccess,
             errorCode,
          });
          return;
        }

        // Check if user is a salesperson
        const { data: salesRecord } = await supabase
          .from('dealer_sales_team')
          .select(`
            id,
            dealer_account_id,
             is_active,
            dealer_accounts!inner(
              id,
              dealership_name,
              status
            )
          `)
          .eq('user_id', user.id)
          .maybeSingle();

        if (salesRecord && salesRecord.dealer_accounts) {
          const dealerInfo = salesRecord.dealer_accounts as unknown as {
            id: string;
            dealership_name: string;
            status: string;
          };
          
           const orgStatus = dealerInfo.status as OrgStatus;
           const memberStatus = salesRecord.is_active ? MEMBER_STATUS.ACTIVE : MEMBER_STATUS.INACTIVE;
           const hasFullAccess = 
             authEmailVerified && 
             orgStatus === ORG_STATUS.APPROVED && 
             memberStatus === MEMBER_STATUS.ACTIVE;
           
           // Compute error code
           let errorCode: AccessErrorCode | null = null;
           if (!authEmailVerified) {
             errorCode = ACCESS_ERROR_CODES.EMAIL_NOT_VERIFIED;
           } else if (orgStatus === ORG_STATUS.SUSPENDED) {
             errorCode = ACCESS_ERROR_CODES.ORG_SUSPENDED;
           } else if (orgStatus === ORG_STATUS.PENDING) {
             errorCode = ACCESS_ERROR_CODES.ORG_NOT_APPROVED;
           } else if (memberStatus === MEMBER_STATUS.INACTIVE) {
             errorCode = ACCESS_ERROR_CODES.MEMBER_INACTIVE;
           }
           
           setScope({
             isDealer: true,
             isDealerAdmin: false,
             isSales: true,
             dealershipId: salesRecord.dealer_account_id,
             dealershipName: dealerInfo.dealership_name,
             salespersonId: salesRecord.id,
             role: DEALER_ROLES.SALES,
             orgStatus,
             memberStatus,
             isEmailVerified: authEmailVerified,
             accountStatus: memberStatus === MEMBER_STATUS.INACTIVE ? 'suspended' : 'active',
             isLoading: false,
             hasFullAccess,
             errorCode,
           });
           return;
        }

        // User is not associated with any dealership
        setScope({
           isDealer: false,
           isDealerAdmin: false,
           isSales: false,
          dealershipId: null,
          dealershipName: null,
           salespersonId: null,
          role: null,
           orgStatus: null,
           memberStatus: null,
           isEmailVerified: authEmailVerified,
           accountStatus: null,
          isLoading: false,
           hasFullAccess: false,
           errorCode: ACCESS_ERROR_CODES.NOT_DEALER_USER,
        });
      } catch (error) {
        console.error('Error fetching dealership scope:', error);
        setScope({
           isDealer: false,
           isDealerAdmin: false,
           isSales: false,
          dealershipId: null,
          dealershipName: null,
           salespersonId: null,
          role: null,
           orgStatus: null,
           memberStatus: null,
           isEmailVerified: authEmailVerified,
           accountStatus: null,
          isLoading: false,
           hasFullAccess: false,
           errorCode: null,
        });
      }
    };

    fetchScope();
   }, [user, authLoading, authEmailVerified]);

  return scope;
}

/**
 * Utility to verify a user has access to a specific dealership
 * Returns true only if the user belongs to the specified dealership
 */
export function verifyDealershipAccess(
  scope: DealershipScope,
  targetDealershipId: string
): boolean {
  if (scope.isLoading) return false;
  if (!scope.dealershipId) return false;
   return scope.dealershipId === targetDealershipId && scope.hasFullAccess;
}
 
 /**
  * Get the blocking reason if dealer doesn't have full access
  */
 export function getDealerAccessBlockReason(
   scope: DealershipScope
 ): 'loading' | 'not_dealer' | 'email_not_verified' | 'org_not_approved' | 'org_suspended' | 'member_inactive' | null {
   if (scope.isLoading) return 'loading';
   if (!scope.isDealer) return 'not_dealer';
   if (!scope.isEmailVerified) return 'email_not_verified';
   if (scope.orgStatus === ORG_STATUS.PENDING) return 'org_not_approved';
   if (scope.orgStatus === ORG_STATUS.SUSPENDED) return 'org_suspended';
   if (scope.memberStatus === MEMBER_STATUS.INACTIVE) return 'member_inactive';
   return null; // Has full access
 }
